/**
 * Relative time formatter for Spanish-language display.
 * Converts ISO timestamps to human-readable relative strings.
 *
 * @module formatters
 */

/**
 * Converts an ISO timestamp string to a Spanish-language relative time string.
 *
 * @param timestamp - An ISO 8601 timestamp string (e.g., "2024-01-15T10:30:00Z")
 * @param now - Optional reference timestamp for "current time" (defaults to Date.now()). Useful for testability.
 * @returns A Spanish relative time string, or empty string if the timestamp is invalid.
 *
 * @example
 * ```ts
 * formatRelativeTime("2024-01-15T10:25:00Z", new Date("2024-01-15T10:30:00Z").getTime())
 * // => "hace 5 minutos"
 * ```
 */
export function formatRelativeTime(timestamp: string, now?: number): string {
  const date = new Date(timestamp);

  if (isNaN(date.getTime())) {
    return '';
  }

  const reference = now ?? Date.now();
  const diffMs = reference - date.getTime();

  // If the timestamp is in the future relative to now, treat as less than a minute
  if (diffMs < 0) {
    return 'hace menos de un minuto';
  }

  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMinutes < 1) {
    return 'hace menos de un minuto';
  }

  if (diffMinutes === 1) {
    return 'hace 1 minuto';
  }

  if (diffMinutes < 60) {
    return `hace ${diffMinutes} minutos`;
  }

  if (diffHours === 1) {
    return 'hace 1 hora';
  }

  if (diffHours < 24) {
    return `hace ${diffHours} horas`;
  }

  if (diffDays === 1) {
    return 'hace 1 día';
  }

  if (diffDays <= 30) {
    return `hace ${diffDays} días`;
  }

  return 'hace más de 30 días';
}
